import React, { createContext, useCallback, useContext, useEffect } from 'react';

const AnalyticsContext = createContext(undefined);

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function buildPageViewData(siteId) {
  return {
    type: 'pageview',
    siteId,
    url: window.location.href,
    path: window.location.pathname,
    title: document.title,
    referrer: document.referrer,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: document.documentElement.clientWidth,
    viewportHeight: document.documentElement.clientHeight,
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timestamp: new Date().toISOString()
  };
}

function buildEventData(siteId, eventName, eventData) {
  return {
    type: 'event',
    siteId,
    eventName,
    eventData,
    url: window.location.href,
    path: window.location.pathname,
    timestamp: new Date().toISOString()
  };
}

function sendAnalytics(apiUrl, data, debug) {
  if (!apiUrl) {
    if (debug && typeof console !== 'undefined') {
      console.log('[NexusAnalytics] Error: apiUrl not configured');
    }
    return;
  }

  if (!isBrowser()) {
    return;
  }

  if (navigator.sendBeacon) {
    const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    navigator.sendBeacon(apiUrl, blob);
  } else {
    fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data),
      keepalive: true
    }).catch((error) => {
      if (debug && typeof console !== 'undefined') {
        console.log('[NexusAnalytics] Error sending data:', error);
      }
    });
  }

  if (debug && typeof console !== 'undefined') {
    console.log('[NexusAnalytics] Tracked:', data.type, data);
  }
}

function trackOutboundLinkClick(trackEvent) {
  return (event) => {
    const link = event.target && event.target.closest ? event.target.closest('a[href]') : null;

    if (!link) {
      return;
    }

    const href = link.getAttribute('href');
    if (!href || href.startsWith('mailto:') || href.startsWith('tel:') || /^javascript:/i.test(href)) {
      return;
    }

    try {
      const targetUrl = new URL(href, window.location.href);
      if (targetUrl.origin !== window.location.origin) {
        trackEvent('outbound_click', {
          url: targetUrl.href,
          anchorText: (link.textContent || '').trim()
        });
      }
    } catch (error) {
      return;
    }
  };
}

function trackFormSubmit(trackEvent) {
  return (event) => {
    const form = event.target;
    if (!form || form.tagName !== 'FORM') {
      return;
    }

    trackEvent('form_submit', {
      formAction: form.getAttribute('action') || '',
      formMethod: form.getAttribute('method') || 'POST',
      formId: form.getAttribute('id') || '',
      formClass: form.getAttribute('class') || ''
    });
  };
}

function trackRouteChange(trackPageView) {
  if (!isBrowser()) {
    return () => {};
  }

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  const dispatchLocationChange = () => {
    window.dispatchEvent(new Event('nexus-locationchange'));
  };

  window.history.pushState = function patchedPushState(...args) {
    const result = originalPushState.apply(this, args);
    dispatchLocationChange();
    return result;
  };

  window.history.replaceState = function patchedReplaceState(...args) {
    const result = originalReplaceState.apply(this, args);
    dispatchLocationChange();
    return result;
  };

  const handleLocationChange = () => {
    trackPageView();
  };

  window.addEventListener('popstate', handleLocationChange);
  window.addEventListener('nexus-locationchange', handleLocationChange);

  return () => {
    window.history.pushState = originalPushState;
    window.history.replaceState = originalReplaceState;
    window.removeEventListener('popstate', handleLocationChange);
    window.removeEventListener('nexus-locationchange', handleLocationChange);
  };
}

export function useAnalytics() {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error('useAnalytics must be used within NexusAnalyticsProvider');
  }

  return context;
}

export function NexusAnalyticsProvider({
  children,
  apiUrl,
  siteId,
  debug = false
}) {
  const trackEvent = useCallback((eventName, eventData = {}) => {
    sendAnalytics(apiUrl, buildEventData(siteId, eventName, eventData), debug);
  }, [apiUrl, siteId, debug]);

  const trackPageView = useCallback(() => {
    sendAnalytics(apiUrl, buildPageViewData(siteId), debug);
  }, [apiUrl, siteId, debug]);

  useEffect(() => {
    if (!isBrowser()) {
      return undefined;
    }

    trackPageView();

    const removeRouteTracking = trackRouteChange(trackPageView);
    const handleClick = trackOutboundLinkClick(trackEvent);
    const handleSubmit = trackFormSubmit(trackEvent);

    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);

    return () => {
      removeRouteTracking();
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('submit', handleSubmit, true);
    };
  }, [trackEvent, trackPageView]);

  return (
    <AnalyticsContext.Provider value={{ trackEvent, trackPageView }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export const NexusAnalytics = {
  trackPageView: (config) => {
    sendAnalytics(config.apiUrl, buildPageViewData(config.siteId), Boolean(config.debug));
  },
  trackEvent: (config, eventName, eventData = {}) => {
    sendAnalytics(
      config.apiUrl,
      buildEventData(config.siteId, eventName, eventData),
      Boolean(config.debug)
    );
  }
};

export default NexusAnalyticsProvider;